import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PresignedAvatar from '../common/PresignedAvatar';

const DROPDOWN_MAX_HEIGHT = 220;

const initialsFromLabel = (label) => {
  const clean = String(label || '').replace(/^You \((.*)\)$/s, '$1').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const WorkflowAssigneeDropdown = ({
  users = [],
  selectedUserId = null,
  selectedUserName = '',
  isOpen = false,
  searchQuery = '',
  onToggleOpen,
  onSearchChange,
  onSelect,
  variant = 'default',
  hideLabel = false,
  layout = 'select',
  caption = '',
  bare = false,
}) => {
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(null);

  const isOnDarkBubble = variant === 'dark';
  const isPersonLayout = layout === 'person';

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => user.label.toLowerCase().includes(query));
  }, [users, searchQuery]);

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < DROPDOWN_MAX_HEIGHT && rect.top > DROPDOWN_MAX_HEIGHT;
    const width = Math.max(isPersonLayout ? 240 : rect.width, 200);
    const left = Math.min(rect.left, Math.max(8, window.innerWidth - width - 8));

    setPanelPosition({
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left,
      width,
      openAbove,
    });
  }, [isPersonLayout]);

  useEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target)
        || panelRef.current?.contains(event.target)
      ) {
        return;
      }
      onToggleOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggleOpen]);

  const triggerClasses = isOnDarkBubble
    ? 'bg-white/15 border-white/30 text-white hover:bg-white/20'
    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-500';

  const placeholderClasses = isOnDarkBubble ? 'text-white/60' : 'text-gray-500 dark:text-gray-400';

  const panelClasses = isOnDarkBubble
    ? 'bg-gray-900 border-white/20 text-white shadow-xl'
    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white shadow-lg';

  const searchInputClasses = isOnDarkBubble
    ? 'bg-white/10 border-white/20 text-white placeholder-white/50 focus:ring-white/30'
    : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-primary-500/50';

  const itemClasses = isOnDarkBubble
    ? 'hover:bg-white/10'
    : 'hover:bg-gray-100 dark:hover:bg-gray-700';

  const selectedItemClasses = isOnDarkBubble
    ? 'bg-white/20'
    : 'bg-violet-50 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200';

  const emptyClasses = isOnDarkBubble ? 'text-white/60' : 'text-gray-500 dark:text-gray-400';

  const selectedUser = users.find((u) => String(u.id) === String(selectedUserId));
  const selectedLabel = selectedUserName || selectedUser?.label;
  const selectedPicture = selectedUser?.profilePicture || '';

  const dropdownPanel = isOpen && panelPosition ? (
    <div
      ref={panelRef}
      className={`rounded-xl border overflow-hidden ${panelClasses}`}
      style={{
        position: 'fixed',
        top: panelPosition.top,
        left: panelPosition.left,
        width: panelPosition.width,
        transform: panelPosition.openAbove ? 'translateY(-100%)' : undefined,
        zIndex: 99999,
      }}
      data-workflow-assign-dropdown-panel
    >
      <div className="p-2 border-b border-inherit">
        <div className="relative">
          <svg
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${placeholderClasses}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search people..."
            className={`w-full h-7 pl-7 pr-2 text-xs rounded-md border focus:outline-none focus:ring-1 ${searchInputClasses}`}
          />
        </div>
      </div>
      <div className="max-h-44 overflow-y-auto py-1" role="listbox">
        {users.length === 0 ? (
          <div className={`px-3 py-2 text-xs ${emptyClasses}`}>
            No active users in this channel
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className={`px-3 py-2 text-xs ${emptyClasses}`}>
            No users match your search
          </div>
        ) : (
          filteredUsers.map((user) => {
            const isSelected = String(selectedUserId) === String(user.id);
            return (
              <button
                key={user.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(user.id);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                  isSelected ? selectedItemClasses : itemClasses
                }`}
              >
                <PresignedAvatar
                  key={user.id}
                  fileKey={user.profilePicture}
                  cacheKey={user.id}
                  name={user.label}
                  initials={initialsFromLabel(user.label)}
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                  fallbackClassName="w-7 h-7 rounded-full flex items-center justify-center bg-primary-500 text-white text-[10px] font-semibold flex-shrink-0"
                />
                <span className="truncate flex-1">{user.label}</span>
                {isSelected && (
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  const personBlock = (
    <div
      className={`group/assignee inline-flex items-center gap-2 min-w-0 ${
        bare
          ? ''
          : `max-w-[13rem] pl-1 pr-1 py-0.5 rounded-full border ${
            isOnDarkBubble
              ? 'bg-black/25 border-white/20 text-white'
              : 'bg-white/90 dark:bg-gray-800/90 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100'
          }`
      }`}
    >
      <PresignedAvatar
        key={selectedUserId || 'unassigned'}
        fileKey={selectedPicture}
        cacheKey={selectedUserId || ''}
        name={selectedLabel || 'Unassigned'}
        initials={selectedLabel ? initialsFromLabel(selectedLabel) : '?'}
        className={`${bare ? 'w-8 h-8' : 'w-6 h-6'} rounded-full object-cover flex-shrink-0`}
        fallbackClassName={`${bare ? 'w-8 h-8 text-[11px]' : 'w-6 h-6 text-[9px]'} rounded-full flex items-center justify-center bg-slate-500 text-white font-semibold flex-shrink-0`}
      />
      <div className="min-w-0">
        <p className={`truncate ${bare ? 'text-[13px] font-semibold text-slate-900 dark:text-white leading-tight' : `text-[11px] font-semibold leading-none ${selectedLabel ? '' : placeholderClasses}`}`}>
          {selectedLabel || 'Unassigned'}
        </p>
        {caption ? (
          <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-tight">{caption}</p>
        ) : null}
      </div>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggleOpen(!isOpen);
        }}
        className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full transition-opacity ${
          isOpen
            ? 'opacity-100'
            : 'opacity-0 group-hover/assignee:opacity-100 focus-visible:opacity-100'
        } ${
          isOnDarkBubble
            ? 'text-white/80 hover:bg-white/15 hover:text-white'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
        }`}
        title="Change assignee"
        aria-label="Change assignee"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
        </svg>
      </button>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={`${isPersonLayout ? 'relative inline-flex' : `w-full basis-full relative ${hideLabel ? '' : 'mt-1'}`} ${isOpen ? 'z-[200]' : ''}`}
      data-workflow-assign-dropdown
    >
      {!hideLabel && !isPersonLayout && (
        <div className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${placeholderClasses}`}>
          Assign to
        </div>
      )}
      {isPersonLayout ? personBlock : (
        <button
          ref={triggerRef}
          type="button"
          onClick={() => onToggleOpen(!isOpen)}
          className={`w-full flex items-center gap-2 h-7 px-2.5 rounded-full border text-xs transition-colors ${triggerClasses}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className={`flex-1 truncate text-left ${selectedLabel ? '' : placeholderClasses}`}>
            {selectedLabel || 'Select user...'}
          </span>
          <svg
            className={`w-3.5 h-3.5 flex-shrink-0 opacity-70 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {dropdownPanel && ReactDOM.createPortal(dropdownPanel, document.body)}
    </div>
  );
};

export default WorkflowAssigneeDropdown;
